import { useId, useState } from 'react';
import { Badge, Button } from 'react-daisyui';
import type { SettingRow } from '@/hooks/useSettings';
import { GenericModal } from '@/components/Modal';

interface SettingsDialogProps {
  row: SettingRow;
  isOn: boolean;
  environment: string | null;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}

// Asks for confirmation before a switch is turned on or off, showing the row's description, with an
// optional reason that is saved in event_logs.
export default function SettingsDialog({
  row,
  isOn,
  environment,
  onConfirm,
  onCancel,
}: SettingsDialogProps) {
  const titleId = useId();
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const action = isOn ? 'Turn off' : 'Turn on';

  // Resets isSaving even when the save fails, so the dialog can be confirmed again.
  const confirm = async () => {
    setIsSaving(true);
    try {
      await onConfirm(reason);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal isOpen onClose={onCancel}>
      <div role='dialog' aria-modal='true' aria-labelledby={titleId}>
        <div className='flex items-center gap-2 pr-8 mb-4'>
          <h2 id={titleId} className='text-lg font-bold'>
            {`${action} "${row.label}"?`}
          </h2>
          {environment && (
            <Badge color='accent' variant='outline' size='sm'>
              {environment}
            </Badge>
          )}
        </div>
        <div className='text-sm space-y-2'>
          <p>
            <code className='font-mono'>{row.key}</code>:{' '}
            {isOn ? 'On → Off' : 'Off → On'}
          </p>
          {row.description && <p>{row.description}</p>}
        </div>
        <label htmlFor={reasonId} className='form-control mt-4'>
          <span className='label label-text'>Reason (optional)</span>
          <textarea
            id={reasonId}
            className='textarea textarea-bordered'
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <div className='mt-6 flex justify-end gap-2'>
          <Button
            type='button'
            color='accent'
            variant='outline'
            size='sm'
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            color='accent'
            size='sm'
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={confirm}
            disabled={isSaving}
            loading={isSaving}
          >
            {action}
          </Button>
        </div>
      </div>
    </GenericModal>
  );
}
